import {useQuery} from '@apollo/client';
import {
    GET_COMMON_CONFIG_BY_KEY,
    GetCommonConfigByKeyResponse,
    GetCommonConfigByKeyVariables,
} from '../../../config/common-config/graphql/commonConfigQueries';

/** commonConfig 中驳回理由文案模版的配置键，configValue 为 JSON 数组 */
export const REJECT_REASON_TEMPLATES_CONFIG_KEY = 'partner_penalty.reject_reason_templates';

export interface RejectReasonTemplate {
    code: string;
    zh?: string;
    en?: string;
    es?: string;
}

type TemplateLang = 'zh' | 'en' | 'es';

const toTemplateLang = (language?: string): TemplateLang => {
    const lang = (language ?? '').toLowerCase();
    if (lang.startsWith('zh')) return 'zh';
    if (lang.startsWith('es')) return 'es';
    return 'en';
};

/** 按当前语言取文案，缺失时依次回退英文、中文 */
export const getTemplateText = (template: RejectReasonTemplate, language?: string): string => {
    const preferred = template[toTemplateLang(language)];
    return (preferred || template.en || template.zh || '').trim();
};

/**
 * 解析配置值。配置由运营在后台维护，结构不可信：非数组、缺 code、三语全空的项一律丢弃，不让弹窗报错。
 */
export const parseRejectReasonTemplates = (configValue?: string | null): RejectReasonTemplate[] => {
    if (!configValue) return [];
    let parsed: unknown;
    try {
        parsed = JSON.parse(configValue);
    } catch {
        return [];
    }
    if (!Array.isArray(parsed)) return [];
    const asText = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);
    return parsed
        .map((item): RejectReasonTemplate | null => {
            if (!item || typeof item !== 'object') return null;
            const raw = item as Record<string, unknown>;
            const code = asText(raw.code);
            const template = {code: code ?? '', zh: asText(raw.zh), en: asText(raw.en), es: asText(raw.es)};
            if (!code || !(template.zh || template.en || template.es)) return null;
            return template;
        })
        .filter((item): item is RejectReasonTemplate => item !== null);
};

/** 读取驳回理由文案模版；配置不存在、未启用或解析失败时返回空列表（下拉框为空，不影响手填） */
export const useRejectReasonTemplates = (skip = false) => {
    const {data, loading} = useQuery<GetCommonConfigByKeyResponse, GetCommonConfigByKeyVariables>(
        GET_COMMON_CONFIG_BY_KEY,
        {variables: {configKey: REJECT_REASON_TEMPLATES_CONFIG_KEY}, skip, fetchPolicy: 'cache-and-network'},
    );
    const config = data?.getCommonConfigByKey;
    const templates = config?.isActive ? parseRejectReasonTemplates(config.configValue) : [];
    return {templates, loading};
};
